import React from "react";
import PropTypes from "prop-types";
import dayjs from "dayjs";
import {
  Alert,
  Box,
  Button,
  Chip,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import Iconify from "../../iconify/Iconify";

const formatDateTime = (value) => {
  if (!value) return "—";
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format("DD.MM.YYYY HH:mm") : "—";
};

const formatCurrency = (value) =>
  new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(
    Number(value) || 0,
  );

const EVENT_PRESET_LABELS = {
  seminar: "Seminar",
  ferienbetreuung: "Ferienbetreuung",
  camp_uebernachtung: "Camp",
  reisecamp: "Reisecamp",
  custom: "Individuell",
};

const CAPACITY_MODE_LABELS = {
  registrations: "Nach Anmeldungen",
  participants: "Nach Teilnehmern",
};

const truncateText = (value, maxLength = 90) => {
  const text = String(value || "").trim();
  if (!text) return "Standardtext";
  if (text.length <= maxLength) return text;
  return `${text.slice(0, maxLength - 3)}...`;
};

function SummaryRow({ label, value }) {
  return (
    <Stack direction="row" justifyContent="space-between" spacing={2}>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" sx={{ textAlign: "right" }}>
        {value}
      </Typography>
    </Stack>
  );
}

SummaryRow.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node.isRequired,
};

export default function StepSummary({ formData, isEdit, onEditStep }) {
  const isBooking = formData.registrationType === "booking";
  const pricingOptions = Array.isArray(formData.pricingOptions)
    ? formData.pricingOptions
    : [];
  const visiblePricingOptions = pricingOptions.filter((option) => {
    const hasLabel = String(option?.label || "").trim().length > 0;
    const hasDescription = String(option?.description || "").trim().length > 0;
    const hasAmount =
      Number.isFinite(Number(option?.amount)) && Number(option.amount) > 0;
    return hasLabel || hasDescription || hasAmount;
  });

  const locationParts = [
    formData.locationName,
    formData.street,
    [formData.zip, formData.city].filter(Boolean).join(" "),
    formData.country,
  ]
    .map((part) => String(part || "").trim())
    .filter(Boolean);
  const registrationConfig = formData.registrationConfig || {};
  const bookingSessions = Array.isArray(formData.sessions)
    ? [...formData.sessions].sort(
        (a, b) => Number(a?.sortOrder || 0) - Number(b?.sortOrder || 0),
      )
    : [];
  const bookingPricingRules = Array.isArray(formData.pricingRules)
    ? formData.pricingRules
    : [];
  const bookingCustomFields = Array.isArray(registrationConfig.customFields)
    ? registrationConfig.customFields
    : [];
  const bookingConsents = Array.isArray(registrationConfig.requiredConsents)
    ? registrationConfig.requiredConsents
    : [];
  const bookingSiblingDiscount =
    registrationConfig.discountRules?.siblingDiscount || {};
  const bookingEarlyBirdDiscount =
    registrationConfig.discountRules?.earlyBirdDiscount || {};
  const publicBookingConfirmationText =
    registrationConfig.publicBookingConfirmationText || "";
  const publicBookingConfirmationTemplateMode =
    registrationConfig.publicBookingConfirmationTemplateMode === "html"
      ? "HTML"
      : "Text";
  const publicBookingConfirmationHtmlTemplate =
    registrationConfig.publicBookingConfirmationHtmlTemplate || "";

  return (
    <Stack spacing={2}>
      <Box>
        <Typography variant="h6">Zusammenfassung</Typography>
        <Typography variant="body2" color="text.secondary">
          Prüfen Sie die Angaben und springen Sie bei Bedarf zurück in einzelne
          Bereiche.
        </Typography>
      </Box>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          spacing={1.5}
        >
          <Stack spacing={0.5}>
            <Typography variant="subtitle2">Event-Typ</Typography>
            <Chip
              label={isBooking ? "Buchungs-Event" : "Standard-Event"}
              color={isBooking ? "info" : "secondary"}
              size="small"
            />
          </Stack>
          <Button
            size="small"
            startIcon={<Iconify icon="solar:pen-2-bold" width={16} />}
            onClick={() => onEditStep("eventType")}
          >
            Bearbeiten
          </Button>
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack spacing={1.25}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            spacing={1.5}
          >
            <Typography variant="subtitle2">Grundlagen</Typography>
            <Button
              size="small"
              startIcon={<Iconify icon="solar:pen-2-bold" width={16} />}
              onClick={() => onEditStep("basicInfo")}
            >
              Bearbeiten
            </Button>
          </Stack>
          <SummaryRow label="Titel" value={formData.eventName || "—"} />
          <SummaryRow label="Status" value={formData.status || "—"} />
          <SummaryRow label="Typ" value={formData.eventType || "—"} />
          <SummaryRow
            label="Zeitraum"
            value={`${formatDateTime(
              formData.eventDateBegin,
            )} bis ${formatDateTime(formData.eventDateEnd)}`}
          />
          <SummaryRow
            label="Ort"
            value={locationParts.length > 0 ? locationParts.join(", ") : "—"}
          />
          <SummaryRow
            label="Max. Teilnehmer"
            value={formData.maxParticipants || "—"}
          />
          <SummaryRow
            label="Tags"
            value={String(formData.tags || "").trim() || "—"}
          />
        </Stack>
      </Paper>

      {!isBooking ? (
        <>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack spacing={1.25}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                spacing={1.5}
              >
                <Typography variant="subtitle2">
                  Sichtbarkeit & Anmeldung
                </Typography>
                <Button
                  size="small"
                  startIcon={<Iconify icon="solar:pen-2-bold" width={16} />}
                  onClick={() => onEditStep("visibility")}
                >
                  Bearbeiten
                </Button>
              </Stack>
              <SummaryRow
                label="Sichtbarkeit"
                value={formData.isPublic ? "Öffentlich" : "Intern"}
              />
              <SummaryRow
                label="Selbstanmeldung"
                value={
                  formData.isPublic
                    ? formData.allowSelfRegistration
                      ? "Aktiv"
                      : "Deaktiviert"
                    : "Nicht relevant (intern)"
                }
              />
              <SummaryRow
                label="Passwort"
                value={formData.password ? "Gesetzt" : "Nicht gesetzt"}
              />
            </Stack>
          </Paper>

          <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack spacing={1.25}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                spacing={1.5}
              >
                <Typography variant="subtitle2">Preise</Typography>
                <Button
                  size="small"
                  startIcon={<Iconify icon="solar:pen-2-bold" width={16} />}
                  onClick={() => onEditStep("pricingStandard")}
                >
                  Bearbeiten
                </Button>
              </Stack>

              {!formData.isFeeBased ? (
                <SummaryRow label="Preis" value="Kostenloses Event" />
              ) : visiblePricingOptions.length === 0 ? (
                <Alert severity="warning">
                  Es sind noch keine gültigen Preisoptionen hinterlegt.
                </Alert>
              ) : (
                <Stack spacing={1}>
                  {visiblePricingOptions.map((option, index) => (
                    <Box key={`${option.optionId || "summary"}-${index}`}>
                      <SummaryRow
                        label={`Option ${index + 1}`}
                        value={`${
                          option.label || "Ohne Titel"
                        } - ${formatCurrency(option.amount)}`}
                      />
                      {option.description ? (
                        <Typography variant="caption" color="text.secondary">
                          {option.description}
                        </Typography>
                      ) : null}
                    </Box>
                  ))}
                </Stack>
              )}

              <SummaryRow
                label="Geschwisterrabatt"
                value={
                  formData.isFeeBased && formData.siblingDiscount?.enabled
                    ? formData.siblingDiscount?.type === "fixed"
                      ? `${Number(formData.siblingDiscount?.value || 0)} EUR`
                      : `${Number(formData.siblingDiscount?.value || 0)} %`
                    : "Nicht aktiv"
                }
              />
            </Stack>
          </Paper>
        </>
      ) : (
        <>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack spacing={1.25}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                spacing={1.5}
              >
                <Typography variant="subtitle2">
                  Anmeldung & Sichtbarkeit
                </Typography>
                <Button
                  size="small"
                  startIcon={<Iconify icon="solar:pen-2-bold" width={16} />}
                  onClick={() => onEditStep("registration")}
                >
                  Bearbeiten
                </Button>
              </Stack>
              <SummaryRow
                label="Sichtbarkeit"
                value={formData.isPublic ? "Öffentlich" : "Intern"}
              />
              <SummaryRow
                label="Öffentliche Anmeldung"
                value={
                  formData.isPublic &&
                  registrationConfig.isPublicRegistrationEnabled
                    ? "Aktiv"
                    : "Deaktiviert"
                }
              />
              <SummaryRow
                label="Freigabe erforderlich"
                value={registrationConfig.requiresApproval ? "Ja" : "Nein"}
              />
              <SummaryRow
                label="Anmeldefrist"
                value={formatDateTime(registrationConfig.registrationDeadline)}
              />
              <SummaryRow
                label="Max. Anmeldungen"
                value={registrationConfig.maxRegistrations || "Kein Limit"}
              />
              <SummaryRow
                label="Kapazitätsmodus"
                value={
                  CAPACITY_MODE_LABELS[registrationConfig.capacityMode] ||
                  CAPACITY_MODE_LABELS.registrations
                }
              />
              <SummaryRow
                label="Eventvorlage"
                value={
                  EVENT_PRESET_LABELS[registrationConfig.eventPreset] ||
                  EVENT_PRESET_LABELS.custom
                }
              />
            </Stack>
          </Paper>

          <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack spacing={1.25}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                spacing={1.5}
              >
                <Typography variant="subtitle2">Sessions</Typography>
                <Button
                  size="small"
                  startIcon={<Iconify icon="solar:pen-2-bold" width={16} />}
                  onClick={() => onEditStep("sessions")}
                >
                  Bearbeiten
                </Button>
              </Stack>
              <SummaryRow
                label="Anzahl Sessions"
                value={bookingSessions.length}
              />
              {bookingSessions.length === 0 ? (
                <Alert severity="warning">
                  Es sind noch keine Sessions hinterlegt.
                </Alert>
              ) : (
                <Stack spacing={0.75}>
                  {bookingSessions.map((session, index) => (
                    <SummaryRow
                      key={
                        session.sessionId ||
                        session._id ||
                        `session-summary-${index}`
                      }
                      label={`Session ${index + 1}`}
                      value={`${
                        session.label || "Ohne Titel"
                      } (${formatDateTime(session.dateStart)})`}
                    />
                  ))}
                </Stack>
              )}
            </Stack>
          </Paper>

          <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack spacing={1.25}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                spacing={1.5}
              >
                <Typography variant="subtitle2">Preise & Rabatte</Typography>
                <Button
                  size="small"
                  startIcon={<Iconify icon="solar:pen-2-bold" width={16} />}
                  onClick={() => onEditStep("pricingBooking")}
                >
                  Bearbeiten
                </Button>
              </Stack>

              {bookingPricingRules.length === 0 ? (
                <Alert severity="warning">
                  Es sind noch keine Preisregeln hinterlegt.
                </Alert>
              ) : (
                <Stack spacing={0.75}>
                  {bookingPricingRules.map((rule, index) => (
                    <SummaryRow
                      key={rule.ruleId || rule._id || `rule-summary-${index}`}
                      label={`Regel ${index + 1}`}
                      value={`${rule.label || "Ohne Titel"} (${formatCurrency(
                        rule.amount,
                      )})`}
                    />
                  ))}
                </Stack>
              )}

              <SummaryRow
                label="Geschwisterrabatt"
                value={
                  bookingSiblingDiscount.enabled
                    ? `${Number(bookingSiblingDiscount.percentage || 0)} %`
                    : "Nicht aktiv"
                }
              />
              <SummaryRow
                label="Frühbucherrabatt"
                value={
                  bookingEarlyBirdDiscount.enabled
                    ? `${
                        bookingEarlyBirdDiscount.type === "fixed"
                          ? `${formatCurrency(
                              bookingEarlyBirdDiscount.amount || 0,
                            )}`
                          : `${Number(
                              bookingEarlyBirdDiscount.percentage || 0,
                            )} %`
                      } bis ${formatDateTime(
                        bookingEarlyBirdDiscount.deadline,
                      )}`
                    : "Nicht aktiv"
                }
              />
            </Stack>
          </Paper>

          <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack spacing={1.25}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                spacing={1.5}
              >
                <Typography variant="subtitle2">Extras</Typography>
                <Button
                  size="small"
                  startIcon={<Iconify icon="solar:pen-2-bold" width={16} />}
                  onClick={() => onEditStep("extras")}
                >
                  Bearbeiten
                </Button>
              </Stack>
              <SummaryRow
                label="Zusatzfelder"
                value={bookingCustomFields.length}
              />
              <SummaryRow
                label="Einwilligungen"
                value={bookingConsents.length}
              />
              <SummaryRow
                label="Überweisungshinweis"
                value={
                  registrationConfig.bankTransferInfo?.enabled
                    ? "Aktiv"
                    : "Deaktiviert"
                }
              />
              <SummaryRow
                label="SEPA-Zustimmungstext"
                value={truncateText(
                  registrationConfig.sepaDirectDebitConsentText,
                )}
              />
              <SummaryRow
                label="Bestätigungsmail-Modus (öffentlich)"
                value={publicBookingConfirmationTemplateMode}
              />
              <SummaryRow
                label="Bestätigungsmail (öffentlich)"
                value={truncateText(
                  publicBookingConfirmationTemplateMode === "HTML"
                    ? publicBookingConfirmationHtmlTemplate ||
                        "(HTML-Vorlage leer)"
                    : publicBookingConfirmationText,
                )}
              />
            </Stack>
          </Paper>
        </>
      )}

      <Typography variant="caption" color="text.secondary">
        {isEdit
          ? "Beim Speichern werden die Änderungen direkt auf das Event angewendet."
          : "Mit dem letzten Schritt wird das Event erstellt."}
      </Typography>
    </Stack>
  );
}

StepSummary.propTypes = {
  formData: PropTypes.shape({
    registrationType: PropTypes.string,
    eventName: PropTypes.string,
    status: PropTypes.string,
    eventType: PropTypes.string,
    eventDateBegin: PropTypes.string,
    eventDateEnd: PropTypes.string,
    locationName: PropTypes.string,
    street: PropTypes.string,
    zip: PropTypes.string,
    city: PropTypes.string,
    country: PropTypes.string,
    maxParticipants: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    tags: PropTypes.string,
    isPublic: PropTypes.bool,
    allowSelfRegistration: PropTypes.bool,
    password: PropTypes.string,
    isFeeBased: PropTypes.bool,
    pricingOptions: PropTypes.arrayOf(
      PropTypes.shape({
        optionId: PropTypes.string,
        label: PropTypes.string,
        description: PropTypes.string,
        amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      }),
    ),
    siblingDiscount: PropTypes.shape({
      enabled: PropTypes.bool,
      type: PropTypes.oneOf(["percentage", "fixed"]),
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
    sessions: PropTypes.arrayOf(
      PropTypes.shape({
        sessionId: PropTypes.string,
        _id: PropTypes.string,
        label: PropTypes.string,
        dateStart: PropTypes.string,
        sortOrder: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      }),
    ),
    pricingRules: PropTypes.arrayOf(
      PropTypes.shape({
        ruleId: PropTypes.string,
        _id: PropTypes.string,
        label: PropTypes.string,
        amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      }),
    ),
    registrationConfig: PropTypes.shape({
      customFields: PropTypes.array,
      requiredConsents: PropTypes.array,
      registrationDeadline: PropTypes.string,
      maxRegistrations: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
      ]),
      capacityMode: PropTypes.string,
      eventPreset: PropTypes.string,
      isPublicRegistrationEnabled: PropTypes.bool,
      requiresApproval: PropTypes.bool,
      bankTransferInfo: PropTypes.shape({
        enabled: PropTypes.bool,
      }),
      sepaDirectDebitConsentText: PropTypes.string,
      publicBookingConfirmationText: PropTypes.string,
      publicBookingConfirmationTemplateMode: PropTypes.oneOf(["text", "html"]),
      publicBookingConfirmationHtmlTemplate: PropTypes.string,
      discountRules: PropTypes.shape({
        siblingDiscount: PropTypes.shape({
          enabled: PropTypes.bool,
          percentage: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        }),
        earlyBirdDiscount: PropTypes.shape({
          enabled: PropTypes.bool,
          type: PropTypes.oneOf(["percentage", "fixed"]),
          percentage: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
          amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
          deadline: PropTypes.string,
        }),
      }),
    }),
  }).isRequired,
  isEdit: PropTypes.bool,
  onEditStep: PropTypes.func.isRequired,
};

StepSummary.defaultProps = {
  isEdit: false,
};
